import { TooltipOverflowMode } from "@fluentui/react";
import React from "react";
import { CenteredCell, CenteredTableCell } from "../../../components/styled";
import { store } from "../../../store/store";
import { formatNumber } from "../../../utils/number";
import { ContextButton } from "../ContextButton";
import { StatusCell } from "../StatusCell";
import CountryCode from "./CountryCode";
import FixedCell from "./FixedCell";
import SicCode from "./SicCode";
import { TooltipComponent } from "components/TooltipComponent";
import { FilterPopoverConfig } from "../../../components/table";
import { Column as ColumnOptionsColumn, Filter } from "../../../components/column-options";
import { TFunction } from "i18next";

// Filter configurations for each column
export const columnFilterConfigs: Record<string, FilterPopoverConfig> = {
  locationName: { type: "text" },
  clientName: { type: "text" },
  siccode: { type: "taxonomy", taxonomyKey: "SicCode" },
  countryCode: { type: "taxonomy", taxonomyKey: "Country" },
  postalCode: { type: "text" },
  city: { type: "text" },
  turnoverInMioEur: { type: "number" },
  tsiinMioEur: { type: "number" },
  pmlinMioEur: { type: "number" },
  emlinMioEur: { type: "number" },
  ebitinMioEur: { type: "number" },
  ygpcminMioEur: { type: "number" },
  deductiblesInMioEur: { type: "number" },
  insurersRaiting: { type: "text" },
  webBasePartnerNumber: { type: "text" },
};

const filterConfigToFilter = (config: FilterPopoverConfig): Filter => {
  switch (config.type) {
    case "text":
      return { type: "text", value: "" };
    case "number":
      return { type: "number", operator: "gt", number1: null, number2: null };
    case "date":
      return { type: "date", operator: "isAfter", date1: null, date2: null };
    case "boolean":
      return { type: "boolean", value: null };
    case "taxonomy":
      return { type: "taxonomy", taxonomyKey: config.taxonomyKey || "", value: [] };
    default:
      return { type: "text", value: "" };
  }
};

/** Column definitions for the ColumnOptions panel */
export const getColumnOptionsColumns = (t: TFunction): ColumnOptionsColumn[] => [
  { key: "assessmentPeriodStartedAt", label: "", isVisible: true, isFixed: true, isPresentInColumnOptions: false },
  { key: "locationName", label: t("ram.locationList.locationName"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.locationName) },
  { key: "options", label: t("greco.options", "Options"), isVisible: true, isAlwaysVisible: true },
  { key: "clientName", label: t("ram.clientsList.clientName"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.clientName) },
  { key: "webBasePartnerNumber", label: t("ram.clientsList.clientId"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.webBasePartnerNumber) },
  { key: "siccode", label: t("ram.locationList.sicCode"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.siccode) },
  { key: "countryCode", label: t("ram.locationList.countryCode"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.countryCode) },
  { key: "postalCode", label: t("ram.locationList.postalCode"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.postalCode) },
  { key: "city", label: t("ram.locationList.city"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.city) },
  { key: "coords", label: t("ram.locationList.coordinates"), isVisible: true },
  { key: "turnoverInMioEur", label: t("ram.locationList.turnoverInMioEur"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.turnoverInMioEur) },
  { key: "tsiinMioEur", label: t("ram.locationList.tsiinMioEur"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.tsiinMioEur) },
  { key: "pmlinMioEur", label: t("ram.locationList.pmlinMioEur"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.pmlinMioEur) },
  { key: "emlinMioEur", label: t("ram.locationList.emlinMioEur"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.emlinMioEur) },
  { key: "ebitinMioEur", label: t("ram.locationList.ebitinMioEur"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.ebitinMioEur) },
  { key: "ygpcminMioEur", label: t("ram.locationList.ygpcminMioEur"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.ygpcminMioEur) },
  { key: "deductiblesInMioEur", label: t("ram.locationList.deductiblesinMioEur"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.deductiblesInMioEur) },
  { key: "insurersRaiting", label: t("ram.locationList.insurersRating"), isVisible: true, filter: filterConfigToFilter(columnFilterConfigs.insurersRaiting) },
];

export const defaultColumns = [
  {
    key: "assessmentPeriodStartedAt",
    fieldName: "assessmentPeriodStartedAt",
    name: "",
    labelKey: "",
    maxWidth: 40,
    minWidth: 40,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",

    onRender: (row, index) => {
      if (!row) return null;
      return (
        <FixedCell no={index} row={row}>
          <StatusCell loc={row} />
        </FixedCell>
      );
    },
  },
  {
    key: "locationName",
    fieldName: "locationName",
    labelKey: "ram.locationList.locationName",
    name: "ram.locationList.locationName",
    maxWidth: 250,
    minWidth: 250,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row, index) => {
      if (!row) return null;
      return (
        <TooltipComponent
          content={row?.locationName}
          overflowMode={TooltipOverflowMode.Parent}
          styles={{
            root: {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              position: "relative",
              bottom: "11px",
              display: "flex",
              width: "100%",
              height: "42px",
            },
          }}
        >
          <FixedCell row={row} no={index}>
            {row?.locationName}
          </FixedCell>
        </TooltipComponent>
      );
    },
  },
  {
    key: "options",
    fieldName: "options",

    labelKey: "",
    name: "",
    minWidth: 40,
    maxWidth: 40,
    isVisible: true,
    isSortable: false,

    onRender: (row, index) => {
      if (!row) return null;
      return (
        <div
          style={{
            height: "41px",
            width: "60px",
            position: "relative",
            bottom: "11px",
          }}
        >
          <FixedCell no={index} row={row}>
            <CenteredCell>
              <ContextButton loc={row} />
            </CenteredCell>
          </FixedCell>
        </div>
      );
    },
  },
  {
    key: "clientName",
    fieldName: "clientName",
    labelKey: "ram.clientsList.clientName",
    name: "ram.clientsList.clientName",
    minWidth: 200,
    maxWidth: 200,
    isVisible: true,
    isResizable: true,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => {
      if (!row) return null;
      return row?.clientName;
    },
  },
  {
    key: "webBasePartnerNumber",
    fieldName: "webBasePartnerNumber",
    labelKey: "ram.clientsList.clientId",
    name: "ram.clientsList.clientId",
    minWidth: 200,
    maxWidth: 200,
    isVisible: true,
    isResizable: true,
    isSortable: true,
    iconName: "Sort",
    onRender: (row) => {
      if (!row) return null;
      return row?.webBasePartnerNumber;
    },
  },
  {
    key: "siccode",
    fieldName: "siccode",
    labelKey: "ram.locationList.sicCode",
    name: "ram.locationList.sicCode",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => {
      if (!row) return null;
      return <SicCode row={row} />;
    },
  },
  {
    key: "countryCode",
    fieldName: "countryCode",
    labelKey: "ram.locationList.countryCode",
    name: "ram.locationList.countryCode",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => {
      if (!row) return null;
      return (
        <CenteredTableCell>
          <CountryCode row={row} />
        </CenteredTableCell>
      );
    },
  },
  {
    key: "postalCode",
    fieldName: "postalCode",
    labelKey: "ram.locationList.postalCode",
    name: "ram.locationList.postalCode",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => <CenteredTableCell>{row?.postalCode}</CenteredTableCell>,
  },
  {
    key: "city",
    fieldName: "city",

    labelKey: "ram.locationList.city",
    name: "ram.locationList.city",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => row?.city,
  },
  {
    key: "coords",
    fieldName: "coords",
    labelKey: "ram.locationList.coordinates",
    name: "ram.locationList.coordinates",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: false,
    isResizable: true,

    onRender: (row) => {
      if (!row?.latitude) return null;

      return `${row?.latitude} , ${row?.longitude}`;
    },
  },
  {
    key: "turnoverInMioEur",
    fieldName: "turnoverInMioEur",
    labelKey: "ram.locationList.turnoverInMioEur",
    name: "ram.locationList.turnoverInMioEur",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => {
      const turnoverInMioEur = row?.turnoverInMioEur;
      if (turnoverInMioEur !== null && turnoverInMioEur !== undefined) {
        return (
          <CenteredTableCell>
            {Number(turnoverInMioEur).toLocaleString("de-DE", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </CenteredTableCell>
        );
      }
    },
  },
  {
    key: "tsiinMioEur",
    fieldName: "tsiinMioEur",
    labelKey: "ram.locationList.tsiinMioEur",
    name: "ram.locationList.tsiinMioEur",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => {
      const tsiinMioEur = row?.tsiinMioEur;
      if (tsiinMioEur !== null && tsiinMioEur !== undefined) {
        return (
          <CenteredTableCell>
            {formatNumber(Number(tsiinMioEur))}
          </CenteredTableCell>
        );
      }
    },
  },
  {
    key: "pmlinMioEur",
    fieldName: "pmlinMioEur",
    labelKey: "ram.locationList.pmlinMioEur",
    name: "ram.locationList.pmlinMioEur",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => {
      const pmlinMioEur = row?.pmlinMioEur;
      if (pmlinMioEur !== null && pmlinMioEur !== undefined) {
        return (
          <CenteredTableCell>
            {formatNumber(Number(pmlinMioEur))}
          </CenteredTableCell>
        );
      }
    },
  },
  {
    key: "emlinMioEur",
    fieldName: "emlinMioEur",
    labelKey: "ram.locationList.emlinMioEur",
    name: "ram.locationList.emlinMioEur",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => {
      const emlinMioEur = row?.emlinMioEur;
      if (emlinMioEur !== null && emlinMioEur !== undefined) {
        return (
          <CenteredTableCell>
            {formatNumber(Number(emlinMioEur))}
          </CenteredTableCell>
        );
      }
    },
  },
  {
    key: "ebitinMioEur",
    fieldName: "ebitinMioEur",
    labelKey: "ram.locationList.ebitinMioEur",
    name: "ram.locationList.ebitinMioEur",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => {
      const ebitinMioEur = row?.ebitinMioEur;
      if (ebitinMioEur !== null && ebitinMioEur !== undefined) {
        return (
          <CenteredTableCell>
            {formatNumber(Number(ebitinMioEur))}
          </CenteredTableCell>
        );
      }
    },
  },
  {
    key: "ygpcminMioEur",
    fieldName: "ygpcminMioEur",
    labelKey: "ram.locationList.ygpcminMioEur",
    name: "ram.locationList.ygpcminMioEur",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => {
      const ygpcminMioEur = row?.ygpcminMioEur;
      if (ygpcminMioEur !== null && ygpcminMioEur !== undefined) {
        return (
          <CenteredTableCell>
            {formatNumber(Number(ygpcminMioEur))}
          </CenteredTableCell>
        );
      }
    },
  },
  {
    key: "deductiblesInMioEur",
    fieldName: "deductiblesInMioEur",
    labelKey: "ram.locationList.deductiblesinMioEur",
    name: "ram.locationList.deductiblesinMioEur",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => {
      const deductiblesInMioEur = row?.deductiblesInMioEur;
      if (deductiblesInMioEur !== null && deductiblesInMioEur !== undefined) {
        return (
          <CenteredTableCell>
            {formatNumber(Number(deductiblesInMioEur))}
          </CenteredTableCell>
        );
      }
    },
  },
  {
    key: "insurersRaiting",
    fieldName: "insurersRaiting",
    labelKey: "ram.locationList.insurersRating",
    name: "ram.locationList.insurersRating",
    minWidth: 150,
    maxWidth: 150,
    isVisible: true,
    isSortable: true,
    iconName: "Sort",
    isResizable: true,

    onRender: (row) => {
      if (!row?.insurersRaiting) return null;
      return row?.insurersRaiting;
    },
  },
];
